import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import WeekdayDatePicker from "../../components/WeekdayDatePicker";
import { useAuth } from "../../contexts/AuthContext";
import { ArrowRight, ArrowLeft, Loader2, MapPin, Package, CalendarDays, AlertCircle, Check, X, Building2, ArrowUpFromLine, Spline, Clock, Info, Wrench, CheckCircle2, PackageX, PackageCheck, Boxes, FileText, Trash2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";

const fmtPrice = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const suppTtc = (ht, coef) => fmtPrice(Math.round(ht * coef * 1.2 * 100) / 100);

export default function NewQuote() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("id");
  const { user } = useAuth();
  const { privacy } = useAuth();
  const [services, setServices] = useState([]);
  const [company, setCompany] = useState(null);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    api.get("/availability").then((r) => setAvailability(r.data)).catch(() => {});
  }, []);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);

  // Step 1 fields
  const [serviceId, setServiceId] = useState("");
  const [arrivalAddress, setArrivalAddress] = useState("");
  const [arrivalCoords, setArrivalCoords] = useState({ lat: null, lng: null });
  const [oversized, setOversized] = useState(0);
  const [deliveryDate, setDeliveryDate] = useState("");

  // calculated
  const [distanceKm, setDistanceKm] = useState(null);
  const [quote, setQuote] = useState(null);
  const [loadingDistance, setLoadingDistance] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const selectedService = services.find((s) => s.id === serviceId);
  const marginCoef = 1 + Number(company?.margin_percent ?? 17.647) / 100;

  // If user changes service or its allowed days don't include current date, reset
  useEffect(() => {
    if (deliveryDate && selectedService?.allowed_weekdays?.length) {
      const idx = new Date(deliveryDate).getDay();
      const names = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
      if (!selectedService.allowed_weekdays.includes(names[idx])) {
        setDeliveryDate("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  // Step 2 fields
  const [step2, setStep2] = useState({
    recipient: { first_name: "", last_name: "", phone: "", email: "", address: "" },
    access: { floor: 0, elevator: false, spiral_staircase: false, custom_slot: false, notes: "" },
    total_packages: 1,
    total_weight_kg: 0,
    oversized_details: [],
    comment: "",
    installer_id: null,
    kitchen_premounted: false,
    kitchen_paid_full: null,
  });
  const [installers, setInstallers] = useState([]);
  const [step2Saving, setStep2Saving] = useState(false);
  const [step2Quote, setStep2Quote] = useState(null);

  // OTP step
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => {
      setServices(r.data);
      if (r.data.length === 1) setServiceId(r.data[0].id);
    });
    api.get("/client/installers").then((r) => setInstallers(r.data)).catch(() => setInstallers([]));
    if (user?.company_id) {
      api.get("/admin/companies").catch(() => null); // admin only - ignore for client
    }
  }, [user]);

  // compute distance when arrival is selected
  useEffect(() => {
    if (!arrivalAddress) return;
    // get company depot via dedicated endpoint? Clients don't have admin/companies access.
    // We'll resolve company via /auth/me + a lightweight endpoint.
    // For simplicity, ask backend to compute distance using company depot. We need depot address.
    // We'll fetch via /auth/me; but company depot not in user. So add a public endpoint? OK simplify: backend exposes a maps/distance taking origin+destination.
    // We need the depot. Approach: fetch a dedicated /client/company endpoint.
  }, [arrivalAddress]);

  // Try to fetch current company depot
  useEffect(() => {
    api.get(`/client/company`).then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  // Resume an existing DRAFT/PENDING_OTP order if ?id= is in URL
  useEffect(() => {
    if (!resumeId) return;
    api.get(`/orders/${resumeId}`).then(async (r) => {
      const o = r.data;
      if (!["DRAFT", "PENDING_OTP"].includes(o.status)) {
        navigate(`/client/orders/${o.id}`, { replace: true });
        return;
      }
      // Check current service still exists & is active
      let currentService = null;
      try {
        const svcs = (await api.get("/services")).data;
        currentService = svcs.find((s) => s.id === o.service_id);
      } catch {}
      if (!currentService) {
        setError("Le service utilisé pour cette demande n'est plus proposé. Merci d'en créer une nouvelle ou de nous contacter.");
        return;
      }
      setOrderId(o.id);
      setPurchaseOrder(o.purchase_order || null);
      setServiceId(o.service_id);
      setArrivalAddress(o.arrival_address || "");
      setArrivalCoords({ lat: o.arrival_lat || null, lng: o.arrival_lng || null });
      setOversized(o.oversized_count || 0);
      setDistanceKm(o.distance_km);
      if (o.delivery_date_wanted) {
        try {
          const d = new Date(o.delivery_date_wanted);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          setDeliveryDate(`${y}-${m}-${dd}`);
        } catch {}
      }
      // Recompute price with CURRENT pricing rules (in case admin updated tariffs)
      try {
        const access = o.access || {};
        const fresh = await api.post("/quote/calculate", {
          service_id: o.service_id,
          distance_km: o.distance_km,
          oversized_count: o.oversized_count || 0,
          delivery_date_wanted: o.delivery_date_wanted,
          floor: Number(access.floor || 0),
          elevator: !!access.elevator,
          spiral_staircase: !!access.spiral_staircase,
          custom_slot: !!access.custom_slot,
        });
        setQuote(fresh.data);
        // Warn if price changed
        const newTtc = Number(fresh.data.total_ttc || 0);
        const oldTtc = Number(o.total_ttc || 0);
        if (Math.abs(newTtc - oldTtc) > 0.01 || fresh.data.on_quote !== o.on_quote) {
          setError(`Attention : nos tarifs ont été mis à jour. Nouveau prix : ${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(newTtc)} TTC (ancien : ${new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(oldTtc)}).`);
        }
        if (o.recipient || o.access) {
          setStep2({
            recipient: o.recipient || { first_name: "", last_name: "", phone: "", email: "", address: o.arrival_address || "" },
            access: o.access || { floor: 0, elevator: false, spiral_staircase: false, custom_slot: false, notes: "" },
            total_packages: o.total_packages || 1,
            total_weight_kg: o.total_weight_kg || 0,
            oversized_details: o.oversized_details || [],
            comment: o.comment || "",
            installer_id: o.installer_id || null,
            kitchen_premounted: typeof o.kitchen_premounted === "boolean" ? o.kitchen_premounted : false,
            kitchen_paid_full: typeof o.kitchen_paid_full === "boolean" ? o.kitchen_paid_full : null,
          });
          setStep2Quote(fresh.data);
          setStep(2);
        }
      } catch {
        setError("Impossible de recalculer le prix avec les tarifs actuels.");
      }
    }).catch(() => {
      setError("Impossible de reprendre cette demande");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeId]);

  const computeDistance = async () => {
    if (!company?.depot_address || !arrivalAddress) {
      setError("Adresse de dépôt ou de livraison manquante");
      return;
    }
    setLoadingDistance(true);
    setError(null);
    try {
      const r = await api.get("/maps/distance", {
        params: { origin: company.depot_address, destination: arrivalAddress },
      });
      setDistanceKm(r.data.distance_km);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoadingDistance(false);
    }
  };

  // Auto-compute distance when address has lat/lng selected
  useEffect(() => {
    if (arrivalCoords.lat && arrivalCoords.lng && company?.depot_address) {
      computeDistance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalCoords.lat, arrivalCoords.lng, company?.depot_address]);

  const canCalculate = serviceId && distanceKm !== null && deliveryDate;

  const onCalculate = async () => {
    setError(null);
    setCalculating(true);
    try {
      const { data } = await api.post("/quote/calculate", {
        service_id: serviceId,
        distance_km: distanceKm,
        oversized_count: Number(oversized) || 0,
        delivery_date_wanted: new Date(deliveryDate).toISOString(),
        floor: 0,
        elevator: false,
        spiral_staircase: false,
        custom_slot: false,
      });
      setQuote(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCalculating(false);
    }
  };

  const goToStep2 = async () => {
    setError(null);
    setCalculating(true);
    try {
      const payload = {
        service_id: serviceId,
        departure_address: company?.depot_address || "",
        departure_lat: company?.depot_lat,
        departure_lng: company?.depot_lng,
        arrival_address: arrivalAddress,
        arrival_lat: arrivalCoords.lat,
        arrival_lng: arrivalCoords.lng,
        distance_km: distanceKm,
        oversized_count: Number(oversized) || 0,
        delivery_date_wanted: new Date(deliveryDate).toISOString(),
      };
      let data;
      if (orderId) {
        // Update existing draft (when user came back from step 2)
        const r = await api.put(`/orders/${orderId}/step1`, payload);
        data = r.data;
        setStep2Quote(null); // force recompute in step 2
      } else {
        const r = await api.post("/orders", payload);
        data = r.data;
        setOrderId(data.id);
        // pre-fill recipient address with arrival
        setStep2((s) => ({ ...s, recipient: { ...s.recipient, address: arrivalAddress } }));
      }
      setStep(2);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setCalculating(false);
    }
  };

  const backToStep1 = () => {
    setError(null);
    setStep(1);
  };

  const step2Payload = () => ({
    recipient: step2.recipient,
    access: step2.access,
    total_packages: Number(step2.total_packages) || 1,
    total_weight_kg: Number(step2.total_weight_kg) || 0,
    oversized_details: step2.oversized_details,
    comment: step2.comment,
    installer_id: step2.installer_id || null,
    kitchen_premounted: step2.kitchen_premounted,
    kitchen_paid_full: step2.kitchen_paid_full,
  });

  const saveStep2 = async () => {
    setError(null);
    if (!isStep2Valid()) {
      setError("Merci de remplir d'abord les coordonnées du destinataire (champs marqués *) avant de recalculer.");
      return;
    }
    setStep2Saving(true);
    try {
      const { data } = await api.put(`/orders/${orderId}/details`, step2Payload());
      setStep2Quote(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setStep2Saving(false);
    }
  };

  const isStep2Valid = () => {
    const r = step2.recipient || {};
    return r.first_name && r.last_name && r.phone && r.email && r.address;
  };

  const requestOtp = async () => {
    setError(null);
    if (!isStep2Valid()) {
      setError("Merci de remplir tous les champs obligatoires du destinataire (marqués *)");
      return;
    }
    if (step2.kitchen_paid_full === null) {
      setError("Merci d'indiquer si la cuisine a été réglée à 100% par le client");
      return;
    }
    setOtpLoading(true);
    try {
      // persist latest step-2 details (incl. payment declaration) before accepting
      const { data } = await api.put(`/orders/${orderId}/details`, step2Payload());
      setStep2Quote(data);
      await api.post(`/orders/${orderId}/accept`);
      setOtpSent(true);
      setStep(3);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setOtpLoading(false);
    }
  };

  const confirmOtp = async () => {
    setError(null);
    setOtpLoading(true);
    try {
      await api.post(`/orders/${orderId}/verify-otp`, { code: otpCode });
      setStep(4);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setOtpLoading(false);
    }
  };

  const cancelDraft = async () => {
    if (orderId) {
      try { await api.post(`/orders/${orderId}/cancel`); } catch {}
    }
    navigate("/client/orders");
  };

  const saveAndExit = async () => {
    // Save current step 2 details (if any data) then navigate to orders list.
    if (orderId && step === 2 && step2.recipient?.first_name) {
      try {
        await api.put(`/orders/${orderId}/details`, step2Payload());
      } catch {}
    }
    navigate("/client/orders");
  };

  return (
    <div data-testid="new-quote-page">
      {/* Progress */}
      <div className="mb-6">
        <div className="lc-small-label mb-2">Nouvelle demande</div>
        <h1 className="display text-3xl sm:text-4xl font-extrabold tracking-tight">Calcul instantané</h1>
        <div className="mt-4 flex items-center gap-2 text-xs">
          {[
            { n: 1, label: "Prix" },
            { n: 2, label: "Détails" },
            { n: 3, label: "Validation" },
            { n: 4, label: "Confirmé" },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div
                data-testid={`progress-step-${s.n}`}
                className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                  step >= s.n
                    ? "bg-[var(--lc-orange)] text-white"
                    : "bg-[var(--lc-surface-alt)] text-[var(--lc-gray)] border border-[var(--lc-border)]"
                }`}
              >
                {step > s.n ? <Check className="w-3.5 h-3.5" /> : s.n}
              </div>
              <span className={`font-semibold ${step >= s.n ? "text-[var(--lc-black)]" : "text-[var(--lc-gray)]"}`}>{s.label}</span>
              {i < 3 && <div className="w-6 h-px bg-[var(--lc-border)]" />}
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div data-testid="error-banner" className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* STEP 1 */}
      {step === 1 && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lc-card p-5 sm:p-6 lg:col-span-2">
            <h2 className="display text-xl font-bold mb-1">Étape 1 — Estimation rapide</h2>
            <p className="text-sm text-[var(--lc-gray)] mb-6">
              Quelques informations suffisent pour obtenir le prix immédiat.
            </p>

            <div className="space-y-5">
              <div>
                <label className="lc-label">Service</label>
                <select
                  data-testid="step1-service-select"
                  className="lc-input"
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="lc-label flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-[var(--lc-orange)]" />
                  Adresse de livraison (destinataire)
                </label>
                <AddressAutocomplete
                  testId="step1-address-input"
                  value={arrivalAddress}
                  onChange={(v) => setArrivalAddress(v)}
                  onSelect={({ formatted_address, lat, lng }) => {
                    setArrivalAddress(formatted_address);
                    setArrivalCoords({ lat, lng });
                  }}
                  placeholder="123 rue, ville…"
                />
                {company?.depot_address && (
                  <p className="text-xs text-[var(--lc-gray)] mt-1">
                    Départ : {company.depot_address}
                  </p>
                )}
                {distanceKm !== null && (
                  <div data-testid="distance-display" className="mt-2 inline-flex items-center gap-2 bg-[var(--lc-orange-light)] text-[var(--lc-orange)] px-2.5 py-1 rounded text-xs font-bold">
                    {loadingDistance ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapPin className="w-3 h-3" />}
                    Distance : {distanceKm} km
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="lc-label flex items-center gap-2">
                    <Package className="w-3.5 h-3.5 text-[var(--lc-orange)]" />
                    Colis hors gabarit
                  </label>
                  <OversizedPicker value={Number(oversized) || 0} onChange={(v) => setOversized(v)} testidPrefix="step1-oversized" />
                  <p className="text-[11px] text-[var(--lc-gray)] mt-2">
                    Ex : plan de travail &ge; 3m, frigo US.
                  </p>
                </div>
                <div>
                  <label className="lc-label flex items-center gap-2">
                    <CalendarDays className="w-3.5 h-3.5 text-[var(--lc-orange)]" />
                    Date souhaitée
                  </label>
                  <WeekdayDatePicker
                    value={deliveryDate}
                    onChange={(iso) => setDeliveryDate(iso)}
                    allowedWeekdays={selectedService?.allowed_weekdays || []}
                    availability={availability}
                    testid="step1-date-input"
                  />
                  {deliveryDate && availability?.capacity > 0 && (() => {
                    const key = String(deliveryDate).slice(0, 10);
                    const rem = Math.max(availability.capacity - (availability.days?.[key] || 0), 0);
                    if (rem === 0) return (
                      <p data-testid="date-slots-info" className="text-[11px] font-bold text-red-600 mt-1.5">Ce jour est complet — choisissez une autre date.</p>
                    );
                    if (rem === 1) return (
                      <p data-testid="date-slots-info" className="text-[11px] font-bold text-orange-600 mt-1.5">Plus qu'une place disponible ce jour !</p>
                    );
                    if (rem < availability.capacity) return (
                      <p data-testid="date-slots-info" className="text-[11px] font-semibold text-green-700 mt-1.5">{rem} places disponibles ce jour</p>
                    );
                    return (
                      <p data-testid="date-slots-info" className="text-[11px] text-[var(--lc-gray)] mt-1.5">Toutes les places sont disponibles ce jour ({availability.capacity})</p>
                    );
                  })()}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  data-testid="step1-calculate-price-button"
                  onClick={onCalculate}
                  disabled={!canCalculate || calculating}
                  className="lc-btn-primary"
                >
                  {calculating ? "Calcul…" : "Calculer le prix"}
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="lc-card p-5 sm:p-6 sticky top-20">
              <div className="lc-small-label mb-3">Votre offre</div>
              {!quote ? (
                <div className="text-sm text-[var(--lc-gray)]">
                  Renseignez les informations pour obtenir une estimation.
                </div>
              ) : quote.on_quote ? (
                <div data-testid="on-quote-result" className="space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 px-3 py-3 rounded">
                    <div className="font-bold text-sm mb-1">Cette prestation est sur devis</div>
                    <p className="text-xs leading-relaxed">
                      Votre demande nécessite un devis personnalisé. Merci de contacter directement :
                    </p>
                    <div className="mt-3 pt-3 border-t border-yellow-200">
                      <div className="font-bold text-sm">Ludovic CAMPILLO</div>
                      <a href="tel:0750832844" className="text-[var(--lc-orange)] font-bold text-base mt-1 inline-block" data-testid="on-quote-phone">
                        07 50 83 28 44
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <div data-testid="quote-result">
                  <div className="text-xs uppercase tracking-widest text-[var(--lc-gray)] mb-1">Forfait</div>
                  <div className="text-lg font-bold mb-3">{quote.package_name}</div>
                  {!privacy && (
                    <>
                      <div className="text-xs uppercase tracking-widest text-[var(--lc-gray)]">Prix TTC</div>
                      <div data-testid="price-ttc" className="text-4xl font-black text-[var(--lc-orange)] tracking-tight">{fmtPrice(quote.total_ttc)}</div>
                      <div data-testid="price-ht" className="text-sm text-[var(--lc-gray)] mt-1">
                        {fmtPrice(quote.total_ht)} HT · TVA {(quote.tva_rate * 100).toFixed(0)}%
                      </div>
                    </>
                  )}
                  {quote.accounting_ref ? (
                    <div className="text-xs text-[var(--lc-gray)] mt-1" data-testid="step1-accounting-ref">
                      Réf. comptable : <span className="font-mono font-bold text-[var(--lc-black)]">{quote.accounting_ref}</span>
                    </div>
                  ) : (
                    <div className="text-xs font-semibold text-[var(--lc-orange)] mt-1" data-testid="step1-accounting-ref-create">
                      Créer un code contre marque
                    </div>
                  )}
                  {!privacy && quote.applied_options?.length > 0 && (
                    <div className="mt-4 space-y-1 text-xs">
                      <div className="lc-small-label">Détail (TTC)</div>
                      <div className="flex justify-between"><span>Forfait base</span><span className="font-semibold">{fmtPrice(Math.round((quote.base_price_ht || 0) * 1.2 * 100) / 100)}</span></div>
                      {quote.applied_options.map((o, i) => (
                        <div key={i} className="flex justify-between text-[var(--lc-gray)]">
                          <span>+ {o.name}{o.quantity > 1 ? ` (×${o.quantity})` : ""}</span>
                          <span>{fmtPrice(Math.round((o.amount || 0) * 1.2 * 100) / 100)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    data-testid="step1-continue-button"
                    onClick={goToStep2}
                    disabled={calculating}
                    className="lc-btn-primary w-full mt-5 flex items-center justify-center gap-2"
                  >
                    Continuer <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[11px] text-[var(--lc-gray)] mt-2">
                    Ce prix est indicatif. Il pourra être ajusté à l'étape suivante selon les conditions d'accès.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <Step2Form
          step2={step2} setStep2={setStep2}
          oversizedCount={Number(oversized) || 0}
          installers={installers}
          onSave={saveStep2} saving={step2Saving}
          quote={step2Quote}
          onAccept={requestOtp} accepting={otpLoading}
          onCancel={cancelDraft}
          onBack={backToStep1}
          onSaveAndExit={saveAndExit}
          marginCoef={marginCoef}
          orderId={orderId}
          purchaseOrder={purchaseOrder}
          setPurchaseOrder={setPurchaseOrder}
        />
      )}

      {/* STEP 3 - OTP */}
      {step === 3 && (
        <div className="max-w-md mx-auto lc-card p-6">
          <h2 className="display text-2xl font-bold mb-1">Validation par email</h2>
          <p className="text-sm text-[var(--lc-gray)] mb-4">
            Nous avons envoyé un code à 6 chiffres à votre adresse. Saisissez-le pour confirmer la commande.
          </p>
          <input
            data-testid="otp-input"
            type="text"
            inputMode="numeric"
            maxLength={6}
            className="lc-input text-center text-3xl tracking-[0.5em] font-bold"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
            placeholder="••••••"
          />
          <div className="flex gap-3 mt-4">
            <button data-testid="otp-cancel-button" onClick={cancelDraft} className="lc-btn-secondary flex-1">
              Annuler
            </button>
            <button data-testid="otp-confirm-button" onClick={confirmOtp} disabled={otpCode.length !== 6 || otpLoading} className="lc-btn-primary flex-1">
              {otpLoading ? "Validation…" : "Valider"}
            </button>
          </div>
          <div className="flex justify-between items-center mt-4 text-xs">
            <button
              onClick={requestOtp}
              disabled={otpLoading}
              data-testid="otp-resend-button"
              className="text-[var(--lc-orange)] font-semibold hover:underline"
            >
              Renvoyer le code
            </button>
            <button
              onClick={saveAndExit}
              data-testid="otp-save-exit-button"
              className="text-[var(--lc-gray)] font-semibold hover:text-[var(--lc-black)] inline-flex items-center gap-1"
            >
              <Clock className="w-3 h-3" /> Reprendre plus tard
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 - SUCCESS */}
      {step === 4 && (
        <div data-testid="success-screen" className="max-w-md mx-auto lc-card p-8 text-center">
          <div className="w-14 h-14 bg-green-100 text-green-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7" strokeWidth={2.5} />
          </div>
          <h2 className="display text-2xl font-bold">Demande envoyée !</h2>
          <p className="text-sm text-[var(--lc-gray)] mt-2">
            Votre demande est en cours de validation. Vous recevrez un email dès que la date de livraison sera confirmée.
          </p>
          <button
            data-testid="success-go-orders"
            onClick={() => navigate("/client/orders")}
            className="lc-btn-primary mt-6"
          >
            Voir mes commandes
          </button>
        </div>
      )}
    </div>
  );
}

function Step2Form({ step2, setStep2, oversizedCount, installers = [], onSave, saving, quote, onAccept, accepting, onCancel, onBack, onSaveAndExit, marginCoef = 1.17647, orderId, purchaseOrder, setPurchaseOrder }) {
  const { privacy } = useAuth();
  const [poUploading, setPoUploading] = useState(false);
  const [poError, setPoError] = useState(null);

  const uploadPo = async (file) => {
    if (!file) return;
    setPoError(null);
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setPoError("Seul un fichier PDF est accepté");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPoError("Fichier trop volumineux (10 Mo maximum)");
      return;
    }
    setPoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/orders/${orderId}/purchase-order`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPurchaseOrder(data.purchase_order);
    } catch (e) {
      setPoError(formatApiError(e));
    } finally {
      setPoUploading(false);
    }
  };

  const removePo = async () => {
    setPoError(null);
    try {
      await api.delete(`/orders/${orderId}/purchase-order`);
      setPurchaseOrder(null);
    } catch (e) {
      setPoError(formatApiError(e));
    }
  };

  const updR = (k, v) => setStep2((s) => ({ ...s, recipient: { ...s.recipient, [k]: v } }));
  const updA = (k, v) => setStep2((s) => ({ ...s, access: { ...s.access, [k]: v } }));

  // Sync oversized_details array length with oversized count
  useEffect(() => {
    setStep2((s) => {
      const arr = [...s.oversized_details];
      while (arr.length < oversizedCount) arr.push({ type: "Plan de travail ≥ 3m", description: "" });
      while (arr.length > oversizedCount) arr.pop();
      return { ...s, oversized_details: arr };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oversizedCount]);

  const OVERSIZED_TYPES = ["Plan de travail ≥ 3m", "Frigo US", "Autre"];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <section className="lc-card p-5 sm:p-6">
          <div className="flex items-center justify-between mb-1">
            <h3 className="display text-lg font-bold">Destinataire</h3>
            <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--lc-orange-light)] text-[var(--lc-orange)] px-2 py-0.5 rounded">À remplir</span>
          </div>
          <p className="text-xs text-[var(--lc-gray)] mb-4">Champs marqués <span className="text-[var(--lc-orange)] font-bold">*</span> obligatoires.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="lc-label">Prénom <span className="text-[var(--lc-orange)]">*</span></label>
              <input data-testid="step2-recipient-first" required className="lc-input" value={step2.recipient.first_name} onChange={(e) => updR("first_name", e.target.value)} />
            </div>
            <div>
              <label className="lc-label">Nom <span className="text-[var(--lc-orange)]">*</span></label>
              <input data-testid="step2-recipient-last" required className="lc-input" value={step2.recipient.last_name} onChange={(e) => updR("last_name", e.target.value)} />
            </div>
            <div>
              <label className="lc-label">Téléphone <span className="text-[var(--lc-orange)]">*</span></label>
              <input data-testid="step2-recipient-phone" required className="lc-input" value={step2.recipient.phone} onChange={(e) => updR("phone", e.target.value)} />
            </div>
            <div>
              <label className="lc-label">Email <span className="text-[var(--lc-orange)]">*</span></label>
              <input data-testid="step2-recipient-email" type="email" required className="lc-input" value={step2.recipient.email} onChange={(e) => updR("email", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="lc-label">Adresse complète <span className="text-[var(--lc-orange)]">*</span></label>
              <input data-testid="step2-recipient-address" required className="lc-input" value={step2.recipient.address} onChange={(e) => updR("address", e.target.value)} />
            </div>
          </div>
        </section>

        <section className="lc-card p-5 sm:p-6" data-testid="step2-installer-section">
          <div className="flex items-center justify-between mb-1">
            <h3 className="display text-lg font-bold flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[var(--lc-orange)]" /> Poseur / monteur
            </h3>
            <span className="text-[10px] font-bold uppercase tracking-widest bg-black/5 text-[var(--lc-gray)] px-2 py-0.5 rounded">Facultatif</span>
          </div>
          <p className="text-xs text-[var(--lc-gray)] mb-4">Associez un poseur / monteur affilié à cette livraison.</p>

          {installers.length === 0 ? (
            <div className="bg-[var(--lc-surface)] rounded-md p-3 text-xs text-[var(--lc-gray)]" data-testid="step2-no-installers">
              Aucun poseur enregistré pour votre entreprise. L'administrateur peut en ajouter depuis l'espace administrateur (onglet « Poseurs &amp; monteurs »).
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="step2-installer-none"
                onClick={() => setStep2((s) => ({ ...s, installer_id: null }))}
                className={`flex items-center gap-3 p-3 rounded-md border text-left transition-colors ${
                  !step2.installer_id
                    ? "border-[var(--lc-orange)] bg-[var(--lc-orange-light)]"
                    : "border-[var(--lc-border)] bg-white hover:border-[var(--lc-orange)]/50"
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${!step2.installer_id ? "bg-white text-[var(--lc-orange)]" : "bg-black/5 text-[var(--lc-gray)]"}`}>
                  <PackageX className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">Sans poseur</div>
                  <div className="text-xs text-[var(--lc-gray)]">Livraison seule</div>
                </div>
                {!step2.installer_id && <CheckCircle2 className="w-5 h-5 text-[var(--lc-orange)] shrink-0" />}
              </button>

              {installers.map((p) => {
                const selected = step2.installer_id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`step2-installer-${p.id}`}
                    onClick={() => setStep2((s) => ({ ...s, installer_id: p.id }))}
                    className={`flex items-center gap-3 p-3 rounded-md border text-left transition-colors ${
                      selected
                        ? "border-[var(--lc-orange)] bg-[var(--lc-orange-light)]"
                        : "border-[var(--lc-border)] bg-white hover:border-[var(--lc-orange)]/50"
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shrink-0 ${selected ? "bg-white text-[var(--lc-orange)]" : "bg-[var(--lc-orange-light)] text-[var(--lc-orange)]"}`}>
                      {(p.first_name?.[0] || "").toUpperCase()}{(p.last_name?.[0] || "").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{p.first_name} {p.last_name}</div>
                      {p.company_name && <div className="text-[11px] font-semibold text-[var(--lc-orange)] truncate">{p.company_name}</div>}
                      <div className="text-xs text-[var(--lc-gray)] truncate">{p.phone || p.email || "—"}</div>
                    </div>
                    {selected && <CheckCircle2 className="w-5 h-5 text-[var(--lc-orange)] shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="lc-card p-5 sm:p-6" data-testid="step2-kitchen-section">
          <h3 className="display text-lg font-bold flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-[var(--lc-orange)]" /> Cuisine
          </h3>
          <p className="text-xs text-[var(--lc-gray)] mb-4">Précisez si la cuisine est livrée prémontée ou non.</p>
          <div className="grid sm:grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="step2-kitchen-premounted"
              onClick={() => setStep2((s) => ({ ...s, kitchen_premounted: true }))}
              className={`flex items-center gap-3 p-3 rounded-md border text-left transition-colors ${
                step2.kitchen_premounted
                  ? "border-[var(--lc-orange)] bg-[var(--lc-orange-light)]"
                  : "border-[var(--lc-border)] bg-white hover:border-[var(--lc-orange)]/50"
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${step2.kitchen_premounted ? "bg-white text-[var(--lc-orange)]" : "bg-black/5 text-[var(--lc-gray)]"}`}>
                <PackageCheck className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Cuisine prémontée</div>
                <div className="text-xs text-[var(--lc-gray)]">Meubles déjà assemblés</div>
              </div>
              {step2.kitchen_premounted && <CheckCircle2 className="w-5 h-5 text-[var(--lc-orange)] shrink-0" />}
            </button>

            <button
              type="button"
              data-testid="step2-kitchen-not-premounted"
              onClick={() => setStep2((s) => ({ ...s, kitchen_premounted: false }))}
              className={`flex items-center gap-3 p-3 rounded-md border text-left transition-colors ${
                !step2.kitchen_premounted
                  ? "border-[var(--lc-orange)] bg-[var(--lc-orange-light)]"
                  : "border-[var(--lc-border)] bg-white hover:border-[var(--lc-orange)]/50"
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${!step2.kitchen_premounted ? "bg-white text-[var(--lc-orange)]" : "bg-black/5 text-[var(--lc-gray)]"}`}>
                <Boxes className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Cuisine non prémontée</div>
                <div className="text-xs text-[var(--lc-gray)]">Meubles en colis à monter</div>
              </div>
              {!step2.kitchen_premounted && <CheckCircle2 className="w-5 h-5 text-[var(--lc-orange)] shrink-0" />}
            </button>
          </div>
        </section>

        <section className="lc-card p-5 sm:p-6">
          <h3 className="display text-lg font-bold mb-4">Colis &amp; poids</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="lc-label">Nombre total de colis</label>
              <input data-testid="step2-total-packages" type="number" min="1" className="lc-input" value={step2.total_packages} onChange={(e) => setStep2((s) => ({ ...s, total_packages: e.target.value }))} />
            </div>
            <div>
              <label className="lc-label">Poids total (kg)</label>
              <input data-testid="step2-total-weight" type="number" min="0" step="1" className="lc-input" value={step2.total_weight_kg} onChange={(e) => setStep2((s) => ({ ...s, total_weight_kg: e.target.value }))} />
            </div>
          </div>

          <div className="mt-4">
            <label className="lc-label">Bon de commande PDF — facultatif</label>
            <p className="text-[11px] text-[var(--lc-gray)] mb-2">Joignez le bon de commande listant les articles : il servira de checklist lors de la récupération à l'entrepôt. PDF, 10 Mo max.</p>
            {poError && (
              <div data-testid="po-error" className="mb-2 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{poError}</span>
              </div>
            )}
            {purchaseOrder ? (
              <div data-testid="po-attached" className="flex items-center justify-between gap-3 border border-[var(--lc-border)] rounded-lg px-3 py-2.5 bg-[var(--lc-surface)]">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 text-[var(--lc-orange)] shrink-0" />
                  <span className="text-sm font-semibold truncate">{purchaseOrder.filename}</span>
                  <span className="text-xs text-[var(--lc-gray)] shrink-0">({(purchaseOrder.size / (1024 * 1024)).toFixed(1)} Mo)</span>
                </div>
                <button type="button" data-testid="po-remove" onClick={removePo} className="p-1.5 hover:bg-red-50 text-red-600 rounded shrink-0" title="Supprimer le fichier">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label data-testid="po-upload-label" className={`flex items-center justify-center gap-2 border border-dashed border-[var(--lc-border-strong)] rounded-lg px-3 py-3 text-sm font-semibold cursor-pointer hover:border-[var(--lc-orange)] hover:text-[var(--lc-orange)] transition-colors ${poUploading ? "opacity-50 pointer-events-none" : ""}`}>
                {poUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {poUploading ? "Envoi en cours…" : "Ajouter le bon de commande (PDF)"}
                <input
                  data-testid="po-file-input"
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => { uploadPo(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
            )}
          </div>
          {oversizedCount > 0 && (
            <div className="mt-4">
              <div className="lc-small-label mb-2">{oversizedCount} colis hors gabarit</div>
              <div className="space-y-2">
                {step2.oversized_details.map((d, i) => (
                  <div key={i} className="grid sm:grid-cols-2 gap-2">
                    <select
                      data-testid={`step2-oversized-type-${i}`}
                      className="lc-input"
                      value={d.type}
                      onChange={(e) => setStep2((s) => {
                        const arr = [...s.oversized_details];
                        arr[i] = { ...arr[i], type: e.target.value };
                        return { ...s, oversized_details: arr };
                      })}
                    >
                      {OVERSIZED_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <input
                      data-testid={`step2-oversized-desc-${i}`}
                      className="lc-input"
                      placeholder="Précisions (facultatif)"
                      value={d.description}
                      onChange={(e) => setStep2((s) => {
                        const arr = [...s.oversized_details];
                        arr[i] = { ...arr[i], description: e.target.value };
                        return { ...s, oversized_details: arr };
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="lc-card p-5 sm:p-6">
          <h3 className="display text-lg font-bold mb-4">Conditions d'accès</h3>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <ToggleCard
              icon={Building2}
              label="Étage"
              description="0 = rez-de-chaussée"
              hint={`Indiquez l'étage du logement (sans compter le RDC). Si pas d'ascenseur, un supplément de ${suppTtc(10, marginCoef)} TTC/étage s'applique.`}
              testid="step2-access-floor-block"
              right={
                <input
                  data-testid="step2-access-floor"
                  type="number"
                  min="0"
                  max="20"
                  className="lc-input"
                  style={{ width: 70, height: "2.25rem", textAlign: "center", padding: "0 0.5rem" }}
                  value={step2.access.floor}
                  onChange={(e) => updA("floor", Number(e.target.value))}
                />
              }
            />
            <ToggleCard
              icon={ArrowUpFromLine}
              label="Ascenseur adapté"
              description="Suffisant pour gros colis"
              hint="L'ascenseur doit accueillir les colis hors gabarit (plan de travail, frigo US…). Si trop étroit ou en panne, ne cochez pas."
              testid="step2-access-elevator-block"
              checked={step2.access.elevator}
              onToggle={(v) => updA("elevator", v)}
              checkboxTestid="step2-access-elevator"
            />
            <ToggleCard
              icon={Spline}
              label="Escalier colimaçon"
              description={`Supplément +${suppTtc(10, marginCoef)} TTC`}
              hint="Escalier en spirale ou très étroit nécessitant une manutention spéciale."
              testid="step2-access-spiral-block"
              checked={step2.access.spiral_staircase}
              onToggle={(v) => updA("spiral_staircase", v)}
              checkboxTestid="step2-access-spiral"
            />
            <ToggleCard
              icon={Clock}
              label="Créneau horaire imposé"
              description={`Supplément +${suppTtc(20, marginCoef)} TTC`}
              hint="Plage horaire précise demandée (ex : 14h-16h). À préciser dans les notes, 48h avant minimum."
              testid="step2-access-slot-block"
              checked={step2.access.custom_slot}
              onToggle={(v) => updA("custom_slot", v)}
              checkboxTestid="step2-access-slot"
            />
          </div>
          <div className="space-y-3">
            <div>
              <label className="lc-label">Notes d'accès / interphone</label>
              <textarea data-testid="step2-access-notes" rows="2" className="lc-input" value={step2.access.notes} onChange={(e) => updA("notes", e.target.value)} />
            </div>
            <div>
              <label className="lc-label">Commentaire libre</label>
              <textarea data-testid="step2-comment" rows="2" className="lc-input" value={step2.comment} onChange={(e) => setStep2((s) => ({ ...s, comment: e.target.value }))} />
            </div>
          </div>
        </section>

        <div className="flex flex-wrap gap-2">
          <button data-testid="step2-back" onClick={onBack} className="lc-btn-secondary">
            <ArrowLeft className="w-4 h-4" /> Modifier l'étape 1
          </button>
          <button data-testid="step2-save-exit" onClick={onSaveAndExit} className="lc-btn-secondary">
            <Clock className="w-4 h-4" /> Reprendre plus tard
          </button>
          <button data-testid="step2-save" onClick={onSave} disabled={saving} className="lc-btn-secondary">
            {saving ? "Mise à jour…" : "Recalculer avec ces détails"}
          </button>
          <button data-testid="step2-cancel" onClick={onCancel} className="lc-btn-secondary" style={{ color: "#DC2626", borderColor: "#FECACA" }}>
            <X className="w-4 h-4" /> Abandonner
          </button>
        </div>
      </div>

      <div>
        <div className="lc-card p-5 sm:p-6 sticky top-20">
          <div className="lc-small-label mb-3">Récapitulatif</div>
          {!quote ? (
            <p className="text-sm text-[var(--lc-gray)]">Cliquez sur « Recalculer » après avoir saisi vos détails pour mettre à jour le prix.</p>
          ) : quote.on_quote ? (
            <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 px-3 py-3 rounded" data-testid="step2-on-quote-result">
              <div className="font-bold text-sm mb-1">Cette prestation est sur devis</div>
              <p className="text-xs leading-relaxed">
                Votre demande nécessite un devis personnalisé. Merci de contacter directement :
              </p>
              <div className="mt-3 pt-3 border-t border-yellow-200">
                <div className="font-bold text-sm">Ludovic CAMPILLO</div>
                <a href="tel:0750832844" className="text-[var(--lc-orange)] font-bold text-base mt-1 inline-block" data-testid="step2-on-quote-phone">
                  07 50 83 28 44
                </a>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--lc-gray)] mb-1">Forfait</div>
              <div className="text-lg font-bold mb-3">{quote.package_name}</div>
              {!privacy && (
                <>
                  <div className="text-xs uppercase tracking-widest text-[var(--lc-gray)]">Prix TTC</div>
                  <div data-testid="step2-price-ttc" className="text-4xl font-black text-[var(--lc-orange)] tracking-tight">{fmtPrice(quote.total_ttc)}</div>
                  <div data-testid="step2-price-ht" className="text-sm text-[var(--lc-gray)] mt-1">
                    {fmtPrice(quote.total_ht)} HT · TVA {(quote.tva_rate * 100).toFixed(0)}%
                  </div>
                </>
              )}
              {quote.accounting_ref ? (
                <div className="text-xs text-[var(--lc-gray)] mt-1" data-testid="step2-accounting-ref">
                  Réf. comptable : <span className="font-mono font-bold text-[var(--lc-black)]">{quote.accounting_ref}</span>
                </div>
              ) : (
                <div className="text-xs font-semibold text-[var(--lc-orange)] mt-1" data-testid="step2-accounting-ref-create">
                  Créer un code contre marque
                </div>
              )}
              {!privacy && quote.applied_options?.length > 0 && (
                <div className="mt-4 space-y-1 text-xs">
                  <div className="lc-small-label">Détail (TTC)</div>
                  <div className="flex justify-between"><span>Forfait base</span><span className="font-semibold">{fmtPrice(Math.round((quote.base_price_ht || 0) * 1.2 * 100) / 100)}</span></div>
                  {quote.applied_options.map((o, i) => (
                    <div key={i} className="flex justify-between text-[var(--lc-gray)]">
                      <span>+ {o.name}{o.quantity > 1 ? ` (×${o.quantity})` : ""}</span>
                      <span>{fmtPrice(Math.round((o.amount || 0) * 1.2 * 100) / 100)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-[var(--lc-border)]" data-testid="step2-payment-consent">
                <div className="text-xs font-bold mb-2">
                  La cuisine a-t-elle été réglée à 100% par le client&nbsp;? <span className="text-[var(--lc-orange)]">*</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="step2-paid-yes"
                    onClick={() => setStep2((s) => ({ ...s, kitchen_paid_full: true }))}
                    className={`px-2 py-2 rounded-md border text-xs font-semibold transition-colors ${
                      step2.kitchen_paid_full === true
                        ? "border-green-500 bg-green-50 text-green-700"
                        : "border-[var(--lc-border)] bg-white text-[var(--lc-dark-gray)] hover:border-green-400"
                    }`}
                  >
                    Oui, réglée à 100%
                  </button>
                  <button
                    type="button"
                    data-testid="step2-paid-no"
                    onClick={() => setStep2((s) => ({ ...s, kitchen_paid_full: false }))}
                    className={`px-2 py-2 rounded-md border text-xs font-semibold transition-colors ${
                      step2.kitchen_paid_full === false
                        ? "border-red-400 bg-red-50 text-red-700"
                        : "border-[var(--lc-border)] bg-white text-[var(--lc-dark-gray)] hover:border-red-300"
                    }`}
                  >
                    Non, pas encore
                  </button>
                </div>
                {step2.kitchen_paid_full === false && (
                  <p data-testid="step2-paid-no-warning" className="text-[11px] leading-relaxed text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
                    Si la cuisine n'est pas réglée le jour de l'enlèvement et ne peut pas être remise, le déplacement sera facturé.
                  </p>
                )}
              </div>
              <button
                data-testid="step2-accept-offer-button"
                onClick={onAccept}
                disabled={accepting || step2.kitchen_paid_full === null}
                className="lc-btn-primary w-full mt-4"
              >
                {accepting ? "Envoi…" : "Accepter l'offre"}
              </button>
              <p className="text-[11px] text-[var(--lc-gray)] mt-2">
                {step2.kitchen_paid_full === null
                  ? "Indiquez le statut de règlement de la cuisine pour continuer."
                  : "Un code à 6 chiffres vous sera envoyé par email pour confirmer."}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ToggleCard({ icon: Icon, label, description, hint, checked, onToggle, right, testid, checkboxTestid }) {
  const isInteractive = onToggle !== undefined;
  const handleClick = () => isInteractive && onToggle(!checked);
  return (
    <div
      data-testid={testid}
      onClick={isInteractive ? handleClick : undefined}
      className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
        isInteractive ? "cursor-pointer hover:border-[var(--lc-orange)]/40" : ""
      } ${checked ? "bg-[var(--lc-orange-soft)] border-[var(--lc-orange)]/40" : "bg-white border-[var(--lc-border)]"}`}
    >
      <div
        className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          checked ? "bg-[var(--lc-orange)] text-white" : "bg-[var(--lc-surface)] text-[var(--lc-gray)]"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold leading-tight flex items-center gap-1.5">
          {label}
          {hint && (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <button type="button" data-testid={`${testid}-hint`} className="text-[var(--lc-gray-light)] hover:text-[var(--lc-orange)] transition-colors" aria-label="Plus d'infos">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px] text-xs leading-relaxed">
                  {hint}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        {description && <div className="text-[11px] text-[var(--lc-gray)] mt-0.5">{description}</div>}
      </div>
      {right ? (
        <div onClick={(e) => e.stopPropagation()}>{right}</div>
      ) : (
        <input
          data-testid={checkboxTestid}
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onToggle?.(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}

function OversizedPicker({ value, onChange, testidPrefix = "oversized" }) {
  const isCustom = value > 9;
  return (
    <div>
      <div className="grid grid-cols-6 sm:grid-cols-11 gap-1.5" role="radiogroup" aria-label="Nombre de colis hors gabarit">
        {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => {
          const active = !isCustom && value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`${testidPrefix}-${n}`}
              onClick={() => onChange(n)}
              className={`h-10 rounded-lg text-sm font-bold border transition-all active:scale-95 ${
                active
                  ? "bg-[var(--lc-orange)] text-white border-[var(--lc-orange)] shadow-sm"
                  : "bg-white text-[var(--lc-dark-gray)] border-[var(--lc-border-strong)] hover:border-[var(--lc-black)]"
              }`}
            >
              {n}
            </button>
          );
        })}
        <button
          type="button"
          role="radio"
          aria-checked={isCustom}
          data-testid={`${testidPrefix}-plus`}
          onClick={() => onChange(value > 9 ? value : 10)}
          className={`h-10 rounded-lg text-xs font-bold border transition-all active:scale-95 col-span-2 sm:col-span-1 ${
            isCustom
              ? "bg-[var(--lc-orange)] text-white border-[var(--lc-orange)] shadow-sm"
              : "bg-white text-[var(--lc-dark-gray)] border-[var(--lc-border-strong)] hover:border-[var(--lc-black)]"
          }`}
        >
          10+
        </button>
      </div>
      {isCustom && (
        <div className="mt-2 flex items-center gap-2 animate-in fade-in slide-in-from-top-1 duration-150">
          <label className="text-xs text-[var(--lc-gray)] font-semibold whitespace-nowrap">Précisez :</label>
          <input
            type="number"
            min="10"
            max="999"
            data-testid={`${testidPrefix}-custom-input`}
            className="lc-input"
            style={{ maxWidth: "120px", height: "2.25rem" }}
            value={value}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!isNaN(n) && n >= 10) onChange(n);
              else if (e.target.value === "") onChange(10);
            }}
          />
          <span className="text-xs text-[var(--lc-gray)]">colis</span>
        </div>
      )}
    </div>
  );
}

